// _components/onboarding/consultation/ConsultStep5.tsx
"use client";

import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { consultSchema, ConsultFormData } from "@/lib/schemas/consultSchema";

type Step5Props = {
  onBack: () => void;
  onSubmit: (data: ConsultFormData) => void;
  isSubmitting: boolean;
  defaultValues?: Partial<ConsultFormData>;
  progressBar?: React.ReactNode;
};

export default function ConsultStep5({
  onBack,
  onSubmit,
  isSubmitting,
  defaultValues,
  progressBar,
}: Step5Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConsultFormData>({
    resolver: zodResolver(consultSchema),
    defaultValues,
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="text-center">
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-semibold text-gray-800"
      >
        Consultation Details
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-2 text-sm text-gray-600"
      >
        Share your details so our professional experts can connect you with the perfect solar
        solution.
      </motion.p>

      {progressBar}

      <div className="mt-8 space-y-5 text-left max-w-xl mx-auto">
        {/* Full Name */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Full Name
          </label>
          <input
            id="name"
            type="text"
            placeholder="John Smith"
            {...register("name")}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.name ? "border-red-500" : "border-gray-200"
              }`}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            placeholder="you@email.com"
            {...register("email")}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.email ? "border-red-500" : "border-gray-200"
              }`}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Phone
          </label>
          <input
            id="phone"
            type="tel"
            placeholder="+234 | 80123456789"
            {...register("phone")}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.phone ? "border-red-500" : "border-gray-200"
              }`}
          />
          {errors.phone && (
            <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>
          )}
        </div>

        {/* Solar Needs */}
        <div>
          <label
            htmlFor="needs"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Solar Needs
          </label>
          <textarea
            id="needs"
            placeholder="Need a system for my shop"
            rows={3}
            {...register("needs")}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.needs ? "border-red-500" : "border-gray-200"
              }`}
          ></textarea>
          {errors.needs && (
            <p className="mt-1 text-xs text-red-500">{errors.needs.message}</p>
          )}
        </div>
      </div>

      <div className="mt-10 flex justify-end gap-4 md:gap-6 flex-col md:flex-row">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-medium hover:bg-gray-100"
        >
          ← Back
        </button>
        <motion.button
          type="submit"
          whileTap={{ scale: 0.95 }}
          className="px-6 py-3 rounded-full bg-green-800 text-white font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit Request"}
        </motion.button>
      </div>
    </form>
  );
}
